import { captureException } from '@sentry/nextjs'

import { AppDispatch, purgePersistedState } from '~/redux/store'
import { homApi } from '~/redux/apis/hom'
import { rewardApi } from '~/redux/apis/reward-api'
import { logoutUser } from '~/auth/slices/auth.slice'

/**
 * Everything that has to go for this browser to hold no session at all. Extracted from
 * `ClassroomAvatarMenu`, which held the only correct copy — the three steps are not interchangeable:
 *
 *   * `logoutUser` drops both tokens and the posthog identity synchronously, then awaits
 *     `GET /v1/auth/logout`. The network call is not decoration: `axiosInstance` runs with
 *     `withCredentials`, so a refresh cookie survives a localStorage-only clear and the next
 *     `/v1/auth/refresh` would hand the session straight back.
 *   * `purgePersistedState` — the persist snapshot outlives the store reset and holds the user.
 *   * `resetApiState` — the query cache still holds whatever `getMe` returned. `rewardApi` is its
 *     own `createApi`, so it needs its own reset: the previous user's cached xp is served instantly
 *     to the next one and reads as a level-up they never earned.
 *
 * Resolves once the network call has settled and never rejects: a failed logout must not strand the
 * caller on a screen it was leaving, and the local clear has already happened by then either way.
 */
export const clearBrowserSession =
  () =>
  async (dispatch: AppDispatch): Promise<void> => {
    const loggedOut = Promise.resolve(dispatch(logoutUser())).catch((error) =>
      captureException(error, { tags: { feature: 'auth', action: 'clear-session' } }),
    )

    purgePersistedState()
    dispatch(homApi.util.resetApiState())
    dispatch(rewardApi.util.resetApiState())

    await loggedOut
  }
